import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "../hooks/useLocalStorage";
import "./Settings.css";

export type VibrationSettings = {
  strength: number; // 0-100, where 0 = disabled, 100 = maximum
};

const DEFAULT_VIBRATION_SETTINGS: VibrationSettings = {
  strength: 10, // Default to 10ms (weak)
};


export type KeyboardConfig = {
  keyRotate: string;
  keyRotateTwice: string;
  keyDown: string;
  keyRight: string;
  keyLeft: string;
  keyDrop: string;
  [key: string]: string;
};

const DEFAULT_CONFIG: KeyboardConfig = {
  keyRotate: "ArrowUp",
  keyRotateTwice: "ShiftRight",
  keyDown: "ArrowDown",
  keyRight: "ArrowRight",
  keyLeft: "ArrowLeft",
  keyDrop: "Space",
};

const KEY_LABELS: Record<keyof KeyboardConfig, string> = {
  keyRotate: "Rotate",
  keyRotateTwice: "Rotate Twice",
  keyDown: "Move Down",
  keyRight: "Move Right",
  keyLeft: "Move Left",
  keyDrop: "Drop",
};

const KEY_DISPLAY_NAMES: Record<string, string> = {
  ArrowUp: "↑",
  ArrowDown: "↓",
  ArrowLeft: "←",
  ArrowRight: "→",
  Space: "Space",
  ShiftRight: "Right Shift",
  ShiftLeft: "Left Shift",
  Enter: "Enter",
  Escape: "Esc",
  Tab: "Tab",
  Backspace: "Backspace",
  Delete: "Delete",
  Home: "Home",
  End: "End",
  PageUp: "Page Up",
  PageDown: "Page Down",
  Insert: "Insert",
};

function getKeyDisplayName(code: string): string {
  if (KEY_DISPLAY_NAMES[code]) {
    return KEY_DISPLAY_NAMES[code];
  }
  if (code.startsWith("Key")) {
    return code.substring(3);
  }
  if (code.startsWith("Digit")) {
    return code.substring(5);
  }
  if (code.startsWith("Numpad")) {
    return `Num ${code.substring(6)}`;
  }
  return code;
}

interface SettingsProps {
  onClose?: () => void;
  showCloseButton?: boolean;
}

const Settings: React.FC<SettingsProps> = ({
  onClose,
  showCloseButton = true,
}) => {
  const navigate = useNavigate();
  const [config, setConfig] = useLocalStorage(
    "keyboardConfig",
    DEFAULT_CONFIG
  ) as [KeyboardConfig, (value: KeyboardConfig) => void];
  const [editingKey, setEditingKey] = useState<keyof KeyboardConfig | null>(
    null
  );
  const [tempConfig, setTempConfig] = useState<KeyboardConfig>(config);
  const [vibrationSettings, setVibrationSettings] = useLocalStorage(
    "vibrationSettings",
    DEFAULT_VIBRATION_SETTINGS
  ) as [VibrationSettings, (value: VibrationSettings) => void];
  const [tempVibrationSettings, setTempVibrationSettings] =
    useState<VibrationSettings>(vibrationSettings);

  useEffect(() => {
    setTempConfig(config);
    setTempVibrationSettings(vibrationSettings);
  }, [config, vibrationSettings]);

  const handleKeyClick = (key: keyof KeyboardConfig) => {
    setEditingKey(key);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (editingKey) {
      event.preventDefault();
      event.stopPropagation();

      const code = event.code;
      if (code === "Escape") {
        setEditingKey(null);
        return;
      }

      // Check if key is already assigned
      const isAlreadyAssigned = Object.values(tempConfig).some(
        (value, index) => {
          const keyName = Object.keys(tempConfig)[
            index
          ] as keyof KeyboardConfig;
          return value === code && keyName !== editingKey;
        }
      );

      if (isAlreadyAssigned) {
        // Show warning or just ignore
        return;
      }

      const newConfig = { ...tempConfig, [editingKey]: code };
      setTempConfig(newConfig);
      setEditingKey(null);
    }
  };

  const handleSave = () => {
    setConfig(tempConfig);
    setVibrationSettings(tempVibrationSettings);
    if (onClose) {
      onClose();
    } else {
      navigate("/");
    }
  };

  const handleReset = () => {
    setTempConfig(DEFAULT_CONFIG);
    setTempVibrationSettings(DEFAULT_VIBRATION_SETTINGS);
  };

  const handleCancel = () => {
    setTempConfig(config);
    setTempVibrationSettings(vibrationSettings);
    if (onClose) {
      onClose();
    } else {
      navigate("/");
    }
  };

  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      if (editingKey && event.code !== "Escape") {
        event.preventDefault();
        event.stopPropagation();

        const code = event.code;

        // Check if key is already assigned
        const isAlreadyAssigned = Object.values(tempConfig).some(
          (value, index) => {
            const keyName = Object.keys(tempConfig)[
              index
            ] as keyof KeyboardConfig;
            return value === code && keyName !== editingKey;
          }
        );

        if (isAlreadyAssigned) {
          return;
        }

        const newConfig = { ...tempConfig, [editingKey]: code };
        setTempConfig(newConfig);
        setEditingKey(null);
      }
    };

    if (editingKey) {
      window.addEventListener("keydown", handleGlobalKeyDown, true);
      return () => {
        window.removeEventListener("keydown", handleGlobalKeyDown, true);
      };
    }
  }, [editingKey, tempConfig]);

  return (
    <div className="settings-container">
      <div className="settings-content">
        <div className="settings-header">
          <h1>Settings</h1>
          {showCloseButton && (
            <button className="settings-close" onClick={handleCancel}>
              ×
            </button>
          )}
        </div>

        <div className="settings-section">
          <h2>Keyboard Configuration</h2>
          <p className="settings-hint">
            Click on a key to change its binding. Press Escape to cancel
            editing.
          </p>

          <div className="keyboard-config">
            {Object.entries(KEY_LABELS).map(([key, label]) => {
              const configKey = key as keyof KeyboardConfig;
              const isEditing = editingKey === configKey;
              const keyCode = tempConfig[configKey];

              return (
                <div key={key} className="key-config-row">
                  <label className="key-label">{label}</label>
                  <button
                    className={`key-button ${isEditing ? "editing" : ""} ${
                      !keyCode ? "unassigned" : ""
                    }`}
                    onClick={() => handleKeyClick(configKey)}
                    onKeyDown={handleKeyDown}
                  >
                    {keyCode ? getKeyDisplayName(keyCode) : "Unassigned"}
                    {isEditing && (
                      <span className="editing-indicator">Press a key...</span>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="settings-section">
          <h2>Vibration Settings</h2>
          <p className="settings-hint">
            Adjust vibration strength for touch controls (mobile devices only)
          </p>

          <div className="vibration-config">
            <div className="vibration-config-row">
              <label className="vibration-label">
                Vibration Strength: {tempVibrationSettings.strength}ms
              </label>
              <div className="vibration-slider-container">
                <input
                  type="range"
                  min="0"
                  max="50"
                  step="1"
                  value={tempVibrationSettings.strength}
                  onChange={(e) =>
                    setTempVibrationSettings({
                      ...tempVibrationSettings,
                      strength: parseInt(e.target.value, 10),
                    })
                  }
                  className="vibration-slider"
                />
                <div className="vibration-slider-labels">
                  <span>Off</span>
                  <span>Weak</span>
                  <span>Medium</span>
                  <span>Strong</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="settings-actions">
          <button className="settings-button secondary" onClick={handleReset}>
            Reset to Defaults
          </button>
          <div className="settings-button-group">
            <button
              className="settings-button secondary"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button className="settings-button primary" onClick={handleSave}>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
export { DEFAULT_CONFIG, getKeyDisplayName };
